import { throttle } from "lodash-es";
import { useEffect, useLayoutEffect, useRef, useState } from "react";

const THROTTLE_TIME = 50;

const useElementWidth = <T extends HTMLElement>(initialValue: number = 0) => {
  const ref = useRef<T | null>(null);
  const [elementWidth, setElementWidth] = useState(initialValue);
  const observerRef = useRef<ResizeObserver | null>(null);
  const observedNodeRef = useRef<T | null>(null);

  useLayoutEffect(() => {
    const node = ref.current;
    if (node === observedNodeRef.current) return;

    observerRef.current?.disconnect();
    observedNodeRef.current = node;

    if (!node) return;

    const updateWidth = throttle(
      () => setElementWidth(node.clientWidth),
      THROTTLE_TIME,
      { leading: true, trailing: true }
    );

    updateWidth();

    const observer = new ResizeObserver(updateWidth);
    observer.observe(node);
    observerRef.current = observer;
  });

  useEffect(() => () => observerRef.current?.disconnect(), []);

  return [ref, elementWidth] as [typeof ref, typeof elementWidth];
};

export default useElementWidth;
